import { SERIES, TRACK } from './chartColors.js';

// T240/T258(2) — the one horizontal proportion bar.
//
// The analytics page grew four of these independently: the disposition
// breakdown, the dismiss-reason list, the RateTable rows, and TodayCard's goal
// meter. They disagreed on radius, height, track colour, and — worse — on where
// colour comes from at all: two painted `bg-brand-600` (a Tailwind class the
// theme layer retargets) while RateTable painted `var(--c-up)` directly, so two
// bars on the same screen could not be the same colour on any theme.
//
// One implementation, colours from chartColors only.
//
//   frac        — 0..1, clamped; the filled share of the track
//   color       — a chartColors token (never a Tailwind class)
//   muted       — small-N honesty: keep the track, draw no fill
//   overlayFrac — 0..1, a SECOND share drawn from the right edge of the same
//                 track (T337)
//
// T337 — the overlay exists for one reading: how much of a source's volume went
// quiet. The rate tables print "12 ghosted" beside a bar showing only the
// advance rate, so the eye ranks by the bar and the worst number on the row is
// the one it cannot see. Drawn from the RIGHT because it is not part of the
// filled share — a segment continuing the fill would read as more advancement.
//
// Muted zeroes it along with the fill. A ghosted count over two applications is
// as unreadable as an advance rate over two, and a bar that keeps one encoding
// while dropping the other says the thin row is half-trustworthy.
export default function MeterBar({
  frac,
  color = SERIES.accent,
  overlayFrac = 0,
  overlayColor = SERIES.warn,
  muted = false,
  height = 8,
  rounded = 'full',
  className = '',
  ariaHidden = true,
  ariaLabel,
}) {
  const pct = muted ? 0 : Math.round(Math.max(0, Math.min(1, frac || 0)) * 100);
  // Clamped against what is LEFT of the track, so the two shares can never
  // overlap into a bar that reads as more than 100%.
  const overlayPct = muted
    ? 0
    : Math.min(100 - pct, Math.round(Math.max(0, Math.min(1, overlayFrac || 0)) * 100));
  const radius = rounded === 'full' ? 'rounded-full' : 'rounded-sm';
  return (
    <div
      className={`relative w-full overflow-hidden ${radius} ${className}`}
      style={{ height, background: TRACK }}
      // The number is always adjacent in text; the bar is a second encoding of
      // it, so by default it is decorative rather than a duplicate announcement.
      aria-hidden={ariaHidden && !ariaLabel ? 'true' : undefined}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
    >
      <div className={`h-full ${radius} transition-all`} style={{ width: `${pct}%`, background: color }} />
      {overlayPct > 0 && (
        <div
          className={`absolute inset-y-0 right-0 ${radius} transition-all`}
          style={{ width: `${overlayPct}%`, background: overlayColor, opacity: 0.55 }}
        />
      )}
    </div>
  );
}
